// <s-marquee> — seamless continuous scroll of its [data-marquee-set] content.
// Repeats the set inside [data-marquee-track] until the track is wider than the viewport plus
// one set, then moves the track by exactly one set (set width + gap) per loop. Because the
// loop restarts at a position that looks identical, there is no gap or visible jump.
// Respects prefers-reduced-motion (no animation) and pauses on hover and keyboard focus.
// One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #ro = null;
  #hovered = false;
  #focused = false;
  #lastWidth = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), opts);

    this.addEventListener("mouseenter", () => { this.#hovered = true; this.#sync(); }, opts);
    this.addEventListener("mouseleave", () => { this.#hovered = false; this.#sync(); }, opts);
    this.addEventListener("focusin", () => { this.#focused = true; this.#sync(); }, opts);
    this.addEventListener("focusout", (e) => {
      if (!this.contains(e.relatedTarget)) { this.#focused = false; this.#sync(); }
    }, opts);

    // Re-measure when the strip changes width (window resize, rotation).
    this.#ro = new ResizeObserver(() => {
      const width = this.clientWidth;
      if (Math.abs(width - this.#lastWidth) < 1) return;
      this.#lastWidth = width;
      this.#setup();
    });
    this.#ro.observe(this);

    // Web fonts change text widths, so measure again once they have loaded.
    document.fonts?.ready.then(() => { if (this.isConnected) this.#setup(); });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ro?.disconnect();
    this.#animation?.cancel();
  }

  #sync() {
    if (!this.#animation) return;
    if (this.#hovered || this.#focused) this.#animation.pause();
    else this.#animation.play();
  }

  #setup() {
    // Keep the loop position across re-measures so a resize doesn't restart it visibly.
    const progress = this.#animation?.effect?.getComputedTiming().progress ?? 0;
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    const set = track?.querySelector("[data-marquee-set]");
    if (!track || !set) return;

    // Remove previous copies; they are rebuilt for the current width.
    track.querySelectorAll("[data-marquee-copy]").forEach((n) => n.remove());
    if (this.#mql.matches) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const period = set.getBoundingClientRect().width + gap;
    if (period <= gap) return;

    // Enough copies that the track always covers the visible area during one full period.
    const viewport = track.parentElement.clientWidth;
    const copies = Math.ceil(viewport / period) + 1;
    for (let i = 0; i < copies; i++) {
      const copy = set.cloneNode(true);
      copy.removeAttribute("data-marquee-set");
      copy.setAttribute("data-marquee-copy", "");
      // Copies are decorative repeats: hidden from assistive tech and the tab order.
      copy.setAttribute("aria-hidden", "true");
      copy.inert = true;
      track.append(copy);
    }

    const speed = parseFloat(this.dataset.speed) || 40;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -period : 0;
    const to = reverse ? 0 : -period;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: (period / speed) * 1000, iterations: Infinity, easing: "linear" },
    );
    if (progress) this.#animation.currentTime = progress * (period / speed) * 1000;
    this.#sync();
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
